import Button from '@ingka/button';
import InlineMessage from '@ingka/inline-message';
import { AxiosError } from 'axios';
import { ChangeEventHandler, FormEvent, ReactElement, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormFieldExternalValid, RuleType } from '../FormField/FormField';
import InputField from '../Skapa/InputField/InputField';
import { useTranslation } from 'react-i18next';
import { isAxiosProblemDetailsError } from './types';
import { StatusCode } from '@mvecom/common/enums/StatusCode';
import { Turnstile, TurnstileInstance } from '@marsidev/react-turnstile';

import styles from './IcapsLoginModalContent.module.scss';
import { ProfilePagesUrls } from '../../utils/Urls';
import { ContinueAsGuest } from './ContinueAsGuest';
import { ButtonType } from '../Skapa/Button/Button';
import { getEnv } from '../../utils/env';
import { getRetailUnitAndLanguage } from '../../network/utils/getRetailUnitAndLanguage';
import { profileAxiosInstance } from '../../network/axios';
import { InlineMessageVariant } from '../Skapa/InlineMessage/InlineMessage';
import { useNetworkStatus } from '../../hooks/useNetworkStatus';
import noticeRedSmallIcon from '@ingka/ssr-icon/paths/notice-red-small';
import classNames from 'classnames';
import { PREFIX } from '../Skapa/constants/constants';

const env = getEnv();

const { retailUnit } = getRetailUnitAndLanguage();

const MARKETS_WITH_TURNSTILE = ['co'];

const canRenderTurnstile = MARKETS_WITH_TURNSTILE.includes(retailUnit);

const SITE_KEYS = {
  qa: '0x4AAAAAAB4csWMKBxrj8bYB',
  development: '3x00000000000000000000FF',
  production: '',
};

const siteKey = SITE_KEYS[env];

interface ILoginPageContentRight {
  isReminder: boolean;
  login: (email: string, password: string) => void;
  loading: boolean;
  loginError: AxiosError | undefined;
}

const EMAIL_VALIDATION = {
  maxLength: 80,
  regex: /^[a-zA-Z0-9_+&*-]+(?:\.[a-zA-Z0-9_+&*-]+)*@(?:[a-zA-Z0-9-]+\.)+[a-zA-Z]{2,}$/,
};

const PASSWORD_VALIDATION = {
  minLength: 1,
};

export const LoginPage = ({ isReminder, login, loading, loginError }: ILoginPageContentRight): ReactElement => {
  const { t } = useTranslation('profile');

  const [emailValid, setEmailValid] = useState(false);
  const [passwordValid, setPasswordValid] = useState(false);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [forceValidateEmail, setForceValidateEmail] = useState(false);
  const [forceValidatePassword, setForceValidatePassword] = useState(false);

  /*
    Turnstile Related stuff
    more info: https://developers.cloudflare.com/turnstile/troubleshooting/testing/
  */

  const [turnstileToken, setTurnstileToken] = useState<string>();
  const [turnstileError, setTurnstileError] = useState<string>();

  const [turnstileFormError, setTurnstileFormError] = useState<boolean>(false);
  const turnstileRef = useRef<TurnstileInstance>(null);

  const isOnline = useNetworkStatus();
  const [showOfflineMsg, setShowOfflineMsg] = useState<boolean>(false);

  const onEmailChange: ChangeEventHandler<HTMLInputElement> = useCallback((e) => {
    setEmail(e.target.value);
    setForceValidateEmail(false);
  }, []);

  const onPasswordChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      setPassword(e.target.value);
      setForceValidatePassword(false);
    },
    [setPassword],
  );

  const problemDetails = useMemo(
    () => (isAxiosProblemDetailsError(loginError) ? loginError.response?.data : undefined),
    [loginError],
  );

  const canContinue = useMemo(
    () => (canRenderTurnstile ? !!turnstileToken && !turnstileError : true),
    [turnstileToken, turnstileError],
  );

  const resetToken = useCallback(() => {
    if (turnstileRef.current && canRenderTurnstile) {
      setTurnstileToken(undefined);
      turnstileRef.current?.reset();
    }
  }, [setTurnstileToken]);

  useEffect(() => resetToken, [problemDetails, resetToken]);

  useEffect(() => {
    if (problemDetails?.type === 'error/account-expired' || problemDetails?.type === 'error/account-migrated') {
      const searchParams = new URLSearchParams();
      if (email) {
        searchParams.append('email', email);
      }
      const search = searchParams.toString();
      globalThis.location.href = ProfilePagesUrls.migrate() + (search ? '?' + search : '');
    }
  }, [email, password, problemDetails?.type]);

  const handleLogin = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setForceValidateEmail(true);
      setForceValidatePassword(true);

      if (!canContinue) {
        setTurnstileFormError(true);
        return;
      }

      if (emailValid && passwordValid && canContinue && !turnstileFormError) {
        login(email, password);
      }
    },
    [email, emailValid, login, password, passwordValid, canContinue, turnstileFormError],
  );

  const goToSignUpPage = useCallback(() => {
    globalThis.location.href = ProfilePagesUrls.signUp();
  }, []);

  const onSuccess = useCallback(
    (token: string) => {
      setTurnstileToken(token);
      setTurnstileFormError(false);
    },
    [setTurnstileToken],
  );

  const onError = useCallback(
    (error: string) => {
      setTurnstileError(error);
    },
    [setTurnstileError],
  );

  useEffect(() => {
    if (canRenderTurnstile && turnstileToken) {
      profileAxiosInstance.defaults.headers.common['cf-turnstile-token'] = turnstileToken;
    } else {
      delete profileAxiosInstance.defaults.headers.common['cf-turnstile-token'];
    }
  }, [turnstileToken]);

  useEffect(() => {
    if (loginError && !isOnline) {
      setShowOfflineMsg(true);
    }
  }, [loginError, isOnline]);

  return (
    <div>
      {isReminder && <ContinueAsGuest loading={loading} />}

      <h2 className={styles.loginPageHeader} data-testid="login-sheet-header">
        {t('loginPage.loginPageHeader')}
      </h2>

      {!isReminder && <p className={styles.loginPageDescription}>{t('loginPage.loginPageHeaderDescription')}</p>}

      {loginError && !showOfflineMsg && problemDetails?.type !== 'error/account-migrated' && (
        <InlineMessage
          prefix={PREFIX}
          title={problemDetails?.status === StatusCode.UNAUTHORIZED ? undefined : t('loginPage.errorMessageTitle')}
          body={
            problemDetails?.status === StatusCode.UNAUTHORIZED
              ? t('loginPage.errorIncorrectInfoMessage')
              : t('loginPage.errorMessage')
          }
          data-testid="error-message"
          variant={InlineMessageVariant.NEGATIVE}
          className={styles.inlineMessage}
        />
      )}

      {loginError && !isOnline && (
        <InlineMessage
          prefix={PREFIX}
          title={t('inlineMessage.offline.title')}
          body={t('inlineMessage.offline.body')}
          data-testid="error-message"
          variant={InlineMessageVariant.NEGATIVE}
          className={styles.inlineMessage}
        />
      )}
      {showOfflineMsg && isOnline && (
        <InlineMessage
          prefix={'PREFIX'}
          title={t('inlineMessage.online.title')}
          body={t('inlineMessage.online.body')}
          data-testid="error-message"
          variant={InlineMessageVariant.POSITIVE}
          className={styles.inlineMessage}
        />
      )}

      <form onSubmit={handleLogin}>
        <fieldset className={styles.fieldset}>
          <FormFieldExternalValid
            validationRules={[
              {
                type: RuleType.MAX_LENGTH,
                value: EMAIL_VALIDATION.maxLength,
                message: t('formInputField.invalidEmail'),
              },
              {
                type: RuleType.REGEX,
                value: EMAIL_VALIDATION.regex,
                message: t('formInputField.invalidEmail'),
              },
            ]}
            hideCharacterLimit
            forceValidate={forceValidateEmail}
            isValid={emailValid}
            setIsValid={setEmailValid}
            className={styles.loginEmail}
          >
            <InputField
              disabled={loading}
              id="login-email"
              autoComplete="email"
              type="text"
              name="email"
              value={email}
              label={t('formInputField.credentialEmail')}
              onChange={onEmailChange}
            />
          </FormFieldExternalValid>
          <FormFieldExternalValid
            validationRules={[
              {
                type: RuleType.MIN_LENGTH,
                value: PASSWORD_VALIDATION.minLength,
                message: t('formInputField.isRequired', { name: t('formInputField.password.name').toLowerCase() }),
              },
            ]}
            hideCharacterLimit
            forceValidate={forceValidatePassword}
            isValid={passwordValid}
            setIsValid={setPasswordValid}
            className={styles.loginPassword}
          >
            <InputField
              disabled={loading}
              id="login-password"
              autoComplete="current-password"
              type="password"
              name="password"
              value={password}
              label={t('formInputField.credentialPassword')}
              onChange={onPasswordChange}
            />
          </FormFieldExternalValid>
        </fieldset>

        <a className={styles.resetPasswordLink} data-testid="reset-password" href={ProfilePagesUrls.resetPassword()}>
          {t('loginPage.forgotPassword')}
        </a>

        {canRenderTurnstile && (
          <div className={styles.turnstileContainer}>
            <Turnstile
              className={classNames(styles.turnstile, {
                [styles.turnstileBoxError]: turnstileFormError,
              })}
              options={{ theme: 'light', size: 'flexible' }}
              ref={turnstileRef}
              siteKey={siteKey}
              onSuccess={onSuccess}
              onError={onError}
            />
            {turnstileFormError && (
              <span className={styles.turnstileFormError}>
                <NoticeRedSmallIcon />
                {t('formInputField.cloudflare.error.helperText')}
              </span>
            )}
          </div>
        )}

        <Button
          className={styles.loginButton}
          disabled={loading}
          prefix={PREFIX}
          fluid
          htmlType="submit"
          loading={loading}
          data-testid="login"
          text={t('loginPage.loginButton')}
          type={isReminder ? ButtonType.SECONDARY : ButtonType.PRIMARY}
        />

        {!isReminder && (
          <>
            <div className={styles.divider}>
              <span>{t('loginPrompt.newIkea.text')}</span>
            </div>

            <Button
              prefix={PREFIX}
              disabled={loading}
              fluid
              onClick={goToSignUpPage}
              data-testid="create-account"
              text={t('loginPage.createAccountButton')}
              type="secondary"
            />
          </>
        )}
      </form>
    </div>
  );
};

const NoticeRedSmallIcon = () => {
  const paths = noticeRedSmallIcon() as React.ReactElement[];
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      className={styles.noticeRedSmallIcon}
      aria-hidden="true"
      focusable="false"
    >
      {paths}
    </svg>
  );
};
